import { createContext, useContext, useLayoutEffect, useState, type ReactNode } from "react";

export type SignalBillPalette = "indigo" | "mint" | "signal" | "registrar" | "mono" | "ledger" | "harbor" | "moss" | "cobalt" | "ultraviolet" | "ember" | "lagoon" | "orchid" | "whiteout" | "blackout";
export type SignalBillTheme = "light" | "dark";

const palettes: readonly SignalBillPalette[] = ["indigo", "mint", "signal", "registrar", "mono", "ledger", "harbor", "moss", "cobalt", "ultraviolet", "ember", "lagoon", "orchid", "whiteout", "blackout"];
const storageKey = "signalbill:appearance";

interface Appearance {
  palette: SignalBillPalette;
  theme: SignalBillTheme;
  setPalette(value: SignalBillPalette): void;
  setTheme(value: SignalBillTheme): void;
}

const AppearanceContext = createContext<Appearance | null>(null);

function storedAppearance(): Pick<Appearance, "palette" | "theme"> {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) ?? "null") as { palette?: string; theme?: string } | null;
    return {
      palette: palettes.includes(saved?.palette as SignalBillPalette) ? saved!.palette as SignalBillPalette : "indigo",
      theme: saved?.theme === "dark" ? "dark" : "light",
    };
  } catch { return { palette: "indigo", theme: "light" }; }
}

export function AppearanceProvider({ children }: { children: ReactNode }) {
  const [initial] = useState(storedAppearance);
  const [palette, setPalette] = useState(initial.palette);
  const [theme, setTheme] = useState(initial.theme);
  useLayoutEffect(() => {
    document.documentElement.dataset.palette = palette;
    document.documentElement.dataset.theme = theme;
    const ground = getComputedStyle(document.documentElement).getPropertyValue("--ground").trim();
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", ground);
    try { localStorage.setItem(storageKey, JSON.stringify({ palette, theme })); } catch { /* Local preference is optional. */ }
  }, [palette, theme]);
  return <AppearanceContext.Provider value={{ palette, theme, setPalette, setTheme }}>{children}</AppearanceContext.Provider>;
}

export function useAppearance(): Appearance {
  const appearance = useContext(AppearanceContext);
  if (!appearance) throw new Error("AppearanceProvider is missing.");
  return appearance;
}
